/**
 * Deployed Images Tab - Manage deployed images and default assignments
 *
 * Features:
 * - List all deployed images across providers
 * - Show which CSPs have each image deployed
 * - View build history with agentic changes
 * - Set default images per provider or instance type
 * - Track usage statistics
 * - Delete images (single + bulk) with CSP confirmation
 * - Monthly storage cost tracking
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../utils/api';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { RefreshCw, Eye, Trash2, AlertCircle, X, Server, Loader2, CloudDownload } from 'lucide-react';
import { DeployedImage, DeployedImagesListResponse, BulkDeleteResponse } from '../../types/deployedImages';
import ImageCard from './ImageCard';

export default function DeployedImagesTab() {
  const [images, setImages] = useState<DeployedImage[]>([]);
  const [totalStorageCost, setTotalStorageCost] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIds, setSelectedImageIds] = useState<Set<string>>(new Set());
  const [showImageCard, setShowImageCard] = useState(false);
  const [activeImage, setActiveImage] = useState<DeployedImage | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<{ ids: string[]; names: string[] } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  // Filters
  const [providerFilter, setProviderFilter] = useState<string | null>(null);
  const [regionFilter, setRegionFilter] = useState<string | null>(null);
  const [defaultsOnly, setDefaultsOnly] = useState(false);

  // Sorting
  const [sortField, setSortField] = useState<'deployed_at' | 'deploy_count' | 'image_name'>('deployed_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    loadImages();
  }, [providerFilter, regionFilter, defaultsOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadImages = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const params = new URLSearchParams();

      if (providerFilter) params.append('provider', providerFilter);
      if (regionFilter) params.append('region', regionFilter);
      if (defaultsOnly) params.append('defaults_only', 'true');

      const response = await axios.get<DeployedImagesListResponse>(
        `${getApiUrl()}/api/v1/deployed-images?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setImages(response.data.images);
      setTotalStorageCost(response.data.total_storage_cost_monthly_usd ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load deployed images');
      console.error('Error loading deployed images:', err);
    } finally {
      setLoading(false);
    }
  };

  const syncFromCloud = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await axios.post(
        `${getApiUrl()}/api/v1/deployed-images/discover?force=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const aws = response.data?.aws?.length ?? 0;
      const gcp = response.data?.gcp?.length ?? 0;
      setSyncResult(`Discovered ${aws} AWS + ${gcp} GCP images`);
      await loadImages();
    } catch (err: any) {
      setSyncResult(`Sync failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncResult(null), 5000);
    }
  };

  const handleRowClick = (image: DeployedImage) => {
    setActiveImage(image);
    setShowImageCard(true);
  };

  const handleSelectImage = (imageId: string) => {
    const newSelected = new Set(selectedImageIds);
    if (newSelected.has(imageId)) {
      newSelected.delete(imageId);
    } else {
      newSelected.add(imageId);
    }
    setSelectedImageIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedImageIds.size === images.length) {
      setSelectedImageIds(new Set());
    } else {
      setSelectedImageIds(new Set(images.map(img => img.id)));
    }
  };

  const handleDeleteSingle = (image: DeployedImage) => {
    setDeleteConfirm({ ids: [image.id], names: [image.image_name] });
  };

  const handleDeleteSelected = () => {
    const selected = images.filter(img => selectedImageIds.has(img.id));
    setDeleteConfirm({
      ids: selected.map(img => img.id),
      names: selected.map(img => img.image_name),
    });
  };

  const executeDelete = async () => {
    if (!deleteConfirm) return;
    const { ids } = deleteConfirm;
    setDeleteConfirm(null);

    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    setDeletingIds(prev => new Set([...Array.from(prev), ...ids]));

    try {
      if (ids.length === 1) {
        await axios.delete(
          `${getApiUrl()}/api/v1/deployed-images/${ids[0]}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        const response = await axios.post<BulkDeleteResponse>(
          `${getApiUrl()}/api/v1/deployed-images/bulk-delete`,
          { image_ids: ids },
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );

        const failures = response.data.results.filter(r => !r.success);
        if (failures.length > 0) {
          setError(`${failures.length} image(s) failed to delete: ${failures.map(f => f.error).join(', ')}`);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingIds(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
      setSelectedImageIds(new Set());
      loadImages();
    }
  };

  const sortedImages = [...images].sort((a, b) => {
    let comparison = 0;

    switch (sortField) {
      case 'deployed_at':
        comparison = new Date(a.deployed_at).getTime() - new Date(b.deployed_at).getTime();
        break;
      case 'deploy_count':
        comparison = a.deploy_count - b.deploy_count;
        break;
      case 'image_name':
        comparison = a.image_name.localeCompare(b.image_name);
        break;
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const getProviderBadgeColor = (provider: string) => {
    const colors: Record<string, string> = {
      aws: 'bg-orange-600/20 text-orange-300 border-orange-500/30',
      gcp: 'bg-blue-600/20 text-blue-300 border-blue-500/30',
      azure: 'bg-cyan-600/20 text-cyan-300 border-cyan-500/30',
      oci: 'bg-red-600/20 text-red-300 border-red-500/30',
      coreweave: 'bg-purple-600/20 text-purple-300 border-purple-500/30'
    };
    return colors[provider] || 'bg-gray-600/20 text-gray-300 border-gray-500/30';
  };

  const getProviderIcon = (provider: string) => {
    const logos: Record<string, string> = {
      aws: '/icons/icons8-aws-48.svg',
      gcp: '/icons/icons8-google-cloud-48.svg',
      azure: '/icons/azure-48.svg',
    };
    if (logos[provider]) {
      return <img src={logos[provider]} alt={provider} className="w-4 h-4" />;
    }
    const emojis: Record<string, string> = {
      oci: '\uD83D\uDD34',
      coreweave: '\u26A1'
    };
    return emojis[provider] || '\uD83D\uDCE6';
  };

  const formatCost = (cost?: number) => {
    if (cost == null || cost === 0) return <span className="text-gray-500">--</span>;
    return <span className="text-gray-300">${cost.toFixed(2)}</span>;
  };

  return (
    <div className="space-y-4">
      {/* Header with Filters */}
      <Card>
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-semibold text-white">Deployed Images</h2>
              <p className="text-sm text-gray-400 mt-1">
                Manage images deployed across cloud providers and configure default assignments
              </p>
            </div>
            <div className="flex items-center gap-3">
              {totalStorageCost > 0 && (
                <div className="px-3 py-2 bg-gray-800/50 rounded-lg border border-gray-700">
                  <span className="text-xs text-gray-400">Total Storage: </span>
                  <span className="text-sm font-semibold text-green-400 tabular-nums">${totalStorageCost.toFixed(2)}/mo</span>
                  <span className="text-xs text-gray-500 ml-1">({images.length} images)</span>
                </div>
              )}
              <Button size="sm" onClick={syncFromCloud} disabled={syncing}>
                {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudDownload className="w-4 h-4" />}
                Sync from Cloud
              </Button>
              <Button size="sm" onClick={loadImages}>
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>
          </div>
          {syncResult && (
            <div className={`px-4 py-2 rounded-lg text-sm ${syncResult.startsWith('Sync failed') ? 'bg-red-900/30 text-red-300 border border-red-800' : 'bg-green-900/30 text-green-300 border border-green-800'}`}>
              {syncResult}
            </div>
          )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select
            value={providerFilter || ''}
            onChange={(e) => setProviderFilter(e.target.value || null)}
            className="px-3 py-2 bg-gray-700 text-white rounded-lg text-sm border border-gray-600 focus:outline-none focus:border-blue-500"
          >
            <option value="">All Providers</option>
            <option value="aws">AWS</option>
            <option value="gcp">GCP</option>
            <option value="azure">Azure</option>
            <option value="oci">OCI</option>
            <option value="coreweave">CoreWeave</option>
          </select>

          <select
            value={regionFilter || ''}
            onChange={(e) => setRegionFilter(e.target.value || null)}
            className="px-3 py-2 bg-gray-700 text-white rounded-lg text-sm border border-gray-600 focus:outline-none focus:border-blue-500"
          >
            <option value="">All Regions</option>
            <option value="us-east-1">us-east-1</option>
            <option value="us-west-2">us-west-2</option>
            <option value="us-central1">us-central1</option>
          </select>

          <label className="flex items-center gap-2 px-3 py-2 bg-gray-700 rounded-lg border border-gray-600 cursor-pointer hover:bg-gray-600">
            <input
              type="checkbox"
              checked={defaultsOnly}
              onChange={(e) => setDefaultsOnly(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-white">Defaults Only</span>
          </label>

          {selectedImageIds.size > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-900/30 border border-blue-500/30 rounded-lg">
              <span className="text-sm text-blue-300">
                {selectedImageIds.size} selected
              </span>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteSelected}
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete Selected
              </Button>
              <button
                onClick={() => setSelectedImageIds(new Set())}
                className="text-xs text-gray-400 hover:text-white"
              >
                Clear
              </button>
            </div>
          )}
        </div>
        </div>
      </Card>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card variant="glass" className="max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-white mb-3">
              Delete {deleteConfirm.ids.length === 1 ? 'Image' : `${deleteConfirm.ids.length} Images`}?
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              This will permanently delete the image{deleteConfirm.ids.length > 1 ? 's' : ''} from the cloud provider and remove {deleteConfirm.ids.length > 1 ? 'them' : 'it'} from the registry.
            </p>
            <div className="bg-gray-900/50 rounded p-3 mb-4 max-h-32 overflow-y-auto">
              {deleteConfirm.names.map((name, i) => (
                <div key={i} className="text-xs text-gray-300 font-mono py-0.5">{name}</div>
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={executeDelete}>
                Delete
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Images Table */}
      {loading ? (
        <Card className="p-12">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            <p className="text-gray-400 text-sm">Loading deployed images...</p>
          </div>
        </Card>
      ) : error ? (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-300">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : images.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <Server className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-2">No deployed images found</p>
            <p className="text-sm text-gray-500">
              Build an image template to see it here once deployed
            </p>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left w-8">
                  <input
                    type="checkbox"
                    checked={selectedImageIds.size === images.length && images.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white"
                  onClick={() => {
                    if (sortField === 'image_name') {
                      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('image_name');
                      setSortDirection('asc');
                    }
                  }}
                >
                  Image Name {sortField === 'image_name' && (sortDirection === 'asc' ? '\u2191' : '\u2193')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Providers
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Arch
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Python / CUDA
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white"
                  onClick={() => {
                    if (sortField === 'deployed_at') {
                      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('deployed_at');
                      setSortDirection('desc');
                    }
                  }}
                >
                  Deployed {sortField === 'deployed_at' && (sortDirection === 'asc' ? '\u2191' : '\u2193')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Default
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white"
                  onClick={() => {
                    if (sortField === 'deploy_count') {
                      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('deploy_count');
                      setSortDirection('desc');
                    }
                  }}
                >
                  Usage {sortField === 'deploy_count' && (sortDirection === 'asc' ? '\u2191' : '\u2193')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Cost/mo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {sortedImages.map((image) => {
                const isDeleting = deletingIds.has(image.id) || image.deployment_status === 'DELETING';
                return (
                  <tr
                    key={image.id}
                    className={`hover:bg-gray-700/30 cursor-pointer transition-colors ${isDeleting ? 'opacity-50' : ''}`}
                    onClick={() => !isDeleting && handleRowClick(image)}
                  >
                    <td
                      className="px-4 py-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selectedImageIds.has(image.id)}
                        onChange={() => handleSelectImage(image.id)}
                        disabled={isDeleting}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white">
                            {image.image_name}
                          </span>
                          {isDeleting && (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-red-900/30 text-red-300 text-xs rounded border border-red-500/30">
                              <div className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                              Deleting
                            </span>
                          )}
                          {!image.template_id && !isDeleting && (
                            <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded border border-gray-600">
                              Manual
                            </span>
                          )}
                        </div>
                        <code className="text-xs text-gray-400 font-mono">
                          {image.image_id}
                        </code>
                        {image.agentic_changes_count > 0 && (
                          <span className="text-xs text-blue-400 mt-1">
                            {image.agentic_changes_count} AI fix{image.agentic_changes_count > 1 ? 'es' : ''} applied
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {image.providers.map((provider) => (
                          <span
                            key={provider}
                            className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border ${getProviderBadgeColor(provider)}`}
                          >
                            <span>{getProviderIcon(provider)}</span>
                            {provider.toUpperCase()}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-300">{image.architecture}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        {image.python_version && (
                          <span className="text-xs text-gray-300">Python {image.python_version}</span>
                        )}
                        {image.cuda_version && (
                          <span className="text-xs text-green-400">CUDA {image.cuda_version}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-300">
                          {new Date(image.deployed_at).toLocaleDateString()}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(image.deployed_at).toLocaleTimeString()}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {(image.default_assignments ?? []).length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {(image.default_assignments ?? [])
                            .slice()
                            .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
                            .map((a, idx) => (
                            <div key={a.id} className="flex items-center gap-1.5">
                              {a.instance_type == null ? (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded font-medium bg-green-600/20 text-green-300 border border-green-500/30">
                                  #{idx + 1} Default
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-1.5 py-0.5 text-xs rounded font-medium bg-gray-600/20 text-gray-300 border border-gray-500/30">
                                  {a.instance_type}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500">&mdash;</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-sm text-white font-medium">
                          {image.deploy_count}
                        </span>
                        {image.last_used_at && (
                          <span className="text-xs text-gray-500">
                            Last: {new Date(image.last_used_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">{formatCost(image.storage_cost_monthly_usd)}</div>
                      {image.size_gb != null && image.size_gb > 0 && (
                        <span className="text-xs text-gray-500">{image.size_gb.toFixed(0)} GB</span>
                      )}
                    </td>
                    <td
                      className="px-4 py-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRowClick(image)}
                          disabled={isDeleting}
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteSingle(image)}
                          disabled={isDeleting || (image.default_assignments ?? []).length > 0}
                          title={(image.default_assignments ?? []).length > 0 ? 'Remove default assignments first' : 'Delete image'}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* Image Card Modal */}
      {showImageCard && activeImage && (
        <ImageCard
          imageId={activeImage.id}
          onClose={() => {
            setShowImageCard(false);
            setActiveImage(null);
          }}
          onDefaultChanged={() => {
            loadImages();  // Refresh table when defaults change
          }}
        />
      )}
    </div>
  );
}
